import { createStore } from "vuex";
import {
    updateProjectInDatabase,
    startProjectsListener as fsStartProjectsListener,
    startUsersListener as fsStartUsersListener,
    startUserListener as fsStartUserListener,
    startApplicationRestartListener as fsStartApplicationRestartListener,
    fetchAllProjects,
    updateMultipleProjectsInDatabase,
} from "@/services/firebaseService"; // Import the modular functions
import {
    ref,
    onValue,
    update,
    push,
    get,
    set,
    remove,
    onDisconnect,
} from "firebase/database";
import { db, auth } from "@/firebaseConfig";
import { signOut } from "firebase/auth";
import {
    getProjectTeamLeaders,
    isProjectActive,
    isProjectTeamLead,
} from "@/utils/projectUtils";
const unsubscribes = {};
// Map of userId to the tail Promise of their task queue
const userQueues = new Map();
/** Last managed project ID set used to decide when to refresh the users listener. */
let previousManagedProjectIdsKey = "";
/**
 * Queue a task so that, for a given userId, tasks run strictly one-at-a-time.
 * Different userIds have independent queues and can run in parallel.
 *
 * Implementation details:
 * - Keeps a Promise "tail" per user in `userQueues`.
 * - Chains the new `task` after the previous tail with `.then(task, task)` so
 *   the queue continues even if the prior task rejected.
 * - Cleans up the tail entry when the queue drains.
 *
 * @param {string} userId
 *    The user whose tasks must be serialized.
 * @param {() => Promise<any>|any} task
 *    A function (can be async) that performs the work. It will be executed
 *    after all previously enqueued tasks for this userId complete (or fail).
 * @returns {Promise<any>}
 *    A promise that resolves/rejects with the result of `task`.
 *
 * @example
 * // Ensure only one DB switch runs at a time for a user
 * enqueueUserTask(userId, async () => {
 *   await update(ref(db), atomicUpdates);
 * });
 */
function enqueueUserTask(userId, task) {
    const previous = userQueues.get(userId) || Promise.resolve();
    const next = previous.then(task, task);
    userQueues.set(
        userId,
        next.finally(() => {
            if (userQueues.get(userId) === next) userQueues.delete(userId);
        })
    );
    return next;
}

/**
 * Vuex store for managing application state.
 */
export default createStore({
    state: {
        user: null, // User object
        allProjects: null,
        managedProjects: null,
        workingProjects: null,
        users: null,
        loading: false,
        activeProject: null,
        startTime: null, // Store startTime as Date object
        isOnLunchBreak: false,
        lastEntryKey: null, // New state for last entry key
        rights: null,
        sessionId: null,
        companies: null,
        priorityResetInProgress: false,
        previousUserProjectPriorities: {},
        overtimeToggleValue: null,
    },
    getters: {
        getUser: (state) => state.user,
        getUsers: (state) => state.users,
        getManagedProjects: (state) => state.managedProjects,
        getWorkingProjects: (state) => state.workingProjects,
        isAuthenticated: (state) =>
            !!state.user && state.user.accountStatus === "active",

        /**
         * Get the loading status from the state.
         * @param {Object} state - Vuex state.
         * @returns {Boolean} - The loading status.
         */
        isLoading: (state) => state.loading,
        getActiveProject: (state) => state.activeProject,
        getStartTime: (state) => state.startTime,
        /**
         * Get the lunch break status from the state.
         * @param {Object} state - Vuex state.
         * @returns {Boolean} - True if on lunch break, false otherwise.
         */
        getIsOnLunchBreak: (state) => state.isOnLunchBreak,
        getRights: (state) => state.rights,
        getAllProjects(state) {
            return state.allProjects;
        },
        /**
         * Get projects assigned to a specific user.
         * Used when viewing another user's data (UserInfo component).
         * This is different from getManagedProjects which returns projects the current user (viewer) can manage.
         *
         * Uses data directly from user.projects which already contains name, order, priority, projectId, etc.
         *
         * @param {string} userId - The ID of the user whose projects to retrieve
         * @returns {Array<Object>} Array of project objects assigned to the user
         */
        getProjectsForUser: (state) => (userId) => {
            if (!userId || !state.users) {
                return [];
            }

            // Find the user
            const allUsers = Array.isArray(state.users)
                ? state.users
                : Object.values(state.users);
            const targetUser = allUsers.find((u) => u?.uid === userId);

            if (!targetUser || !targetUser.projects) {
                return [];
            }

            // user.projects already contains all the data we need:
            // { projectId: { name, order, priority, projectId, status? } }
            // Object.values() converts the object to an array, which is what components expect
            return Object.values(targetUser.projects);
        },
        getCompanies: (state) => state.companies,
    },
    mutations: {
        /**
         * Adds a user to the `activeUsers` map of the specified project in the local Vuex state.
         *
         * @param {object} state - The Vuex state object.
         * @param {object} payload - The payload containing identifiers and user data.
         * @param {string} payload.projectId - The ID of the project to which the user should be added.
         * @param {string} payload.userId - The ID of the user to add.
         * @param {object} payload.user - An object containing the user’s details (e.g. `{ name, surname }`).
         */
        addActiveUserLocal(state, { projectId, userId, user }) {
            // We maintain two parallel lists of projects: "working" and "managed"
            [state.workingProjects, state.managedProjects].forEach(
                (projectsList) => {
                    if (!Array.isArray(projectsList)) return;
                    // Find the target project in this list
                    const project = projectsList.find(
                        (p) => p.projectId === projectId
                    );
                    if (!project) return;
                    // Initialize the activeUsers map if it doesn't exist
                    if (!project.activeUsers) {
                        project.activeUsers = {};
                    }
                    // Add or update this user in the project's activeUsers
                    project.activeUsers[userId] = user;
                }
            );
        },

        /**
         * Removes a user from the `activeUsers` map of the specified project in the local Vuex state.
         *
         * @param {object} state - The Vuex state object.
         * @param {object} payload - The payload containing identifiers.
         * @param {string} payload.projectId - The ID of the project from which the user should be removed.
         * @param {string} payload.userId - The ID of the user to remove.
         */
        removeActiveUserLocal(state, { projectId, userId }) {
            [state.workingProjects, state.managedProjects].forEach(
                (projectsList) => {
                    if (!Array.isArray(projectsList)) return;
                    // Find the target project in this list
                    const project = projectsList.find(
                        (p) => p.projectId === projectId
                    );
                    if (!project || !project.activeUsers) return;
                    // Remove the user from the project's activeUsers map
                    delete project.activeUsers[userId];
                }
            );
        },
        setCompanies(state, companies) {
            state.companies = companies;
        },
        resetPrioritiesOfProjects(state, projectIds) {
            projectIds.forEach((projectId) => {
                const project = state.managedProjects.find(
                    (p) => p.projectId === projectId
                );
                if (project) {
                    project.priority = "Normal";
                }
            });
        },
        setSessionId(state, sessionId) {
            state.sessionId = sessionId;
        },
        setAllProjects(state, projects) {
            state.allProjects = projects;
        },
        setManagedProjects(state, projects) {
            state.managedProjects = projects;
        },
        setWorkingProjects(state, projects) {
            state.workingProjects = projects;
        },
        setRights(state, rights) {
            state.rights = rights;
        },
        setUsers(state, users) {
            state.users = users;
        },
        setUser(state, user) {
            // Before updating the user, preserve current projects priorities for comparison
            // This allows App.vue watcher to detect priority changes and show notifications
            if (state.user && state.user.projects && user && user.projects) {
                // Ensure previousUserProjectPriorities exists
                if (!state.previousUserProjectPriorities) {
                    state.previousUserProjectPriorities = {};
                }
                
                // Save current priorities to snapshot BEFORE updating
                // This ensures the watcher can compare old (saved here) vs new (from updated user)
                Object.keys(state.user.projects).forEach((projectId) => {
                    // Always save the current state before updating, so watcher can detect changes
                    state.previousUserProjectPriorities[projectId] = {
                        ...state.user.projects[projectId],
                    };
                });
            }
            
            // Update the user object (this triggers the watcher which compares against previousUserProjectPriorities)
            state.user = user;
        },
        setActiveProject(state, project) {
            state.activeProject = project;
        },
        /**
         * Sets the start time in the state.
         * @param {Object} state - Vuex state.
         * @param {Date} startTime - The start time as a Date object.
         */
        setStartTime(state, startTime) {
            state.startTime = startTime;
        },
        setIsOnLunchBreak(state, isOnLunchBreak) {
            state.isOnLunchBreak = isOnLunchBreak;
        },
        setOvertimeToggleValue(state, value) {
            state.overtimeToggleValue = value;
        },
        setUserStatus(state, status) {
            if (state.user) {
                state.user.userStatus = status;
            }
        },
        /**
         * Sets the loading status in the state.
         * @param {Object} state - Vuex state.
         * @param {Boolean} status - The loading status.
         */
        setLoading(state, status) {
            state.loading = status;
        },
        setLastEntryKey(state, entryKey) {
            state.lastEntryKey = entryKey;
        },

        /**
         * Sets the account status of a user in the state.
         * @param {Object} state - Vuex state.
         * @param {Object} payload - Contains userId and status.
         * @param {String} payload.userId - The user ID.
         * @param {String} payload.status - The new account status.
         */
        setAccountStatus(state, { userId, status }) {
            if (!state.users) return;

            const apply = (userObj) => {
                if (userObj) userObj.accountStatus = status;
            };

            if (Array.isArray(state.users)) {
                // users stored as an array
                apply(state.users.find((user) => user?.uid === userId));
            } else {
                // users stored as a map (maybe keyed by uid, maybe by something else)
                apply(
                    state.users[userId] ||
                        Object.values(state.users).find(
                            (user) => user?.uid === userId
                        )
                );
            }

            // keep the signed-in user in sync too
            if (state.user?.uid === userId) {
                state.user.accountStatus = status;
            }
        },

        /**
         * Updates a project in the Vuex state.
         * @param {Object} state - Vuex state.
         * @param {Object} payload - Contains projectId and updatedProject.
         * @param {String} payload.projectId - The project ID.
         * @param {Object} payload.updatedProject - The updated project data.
         */
        updateProject(state, { projectId, updatedProject }) {
            const patchList = (list) => {
                if (!Array.isArray(list)) return;
                const idx = list.findIndex((p) => p.projectId === projectId);
                if (idx === -1) return;
                list[idx] = { ...list[idx], ...updatedProject };
            };
            patchList(state.managedProjects);
            patchList(state.workingProjects);
            patchList(state.allProjects);
        },
        /**
         * Updates all users' project priorities in the Vuex state.
         * @param {Object} state - Vuex state.
         * @param {Object} updatedProjects - Updated projects keyed by user ID.
         */
        updateUsersProjects(state, updatedProjects) {
            if (state.users) {
                for (const userId in updatedProjects) {
                    const user = state.users.find((user) => user.id === userId);
                    if (user && user.projects) {
                        Object.keys(updatedProjects[userId]).forEach(
                            (projectId) => {
                                if (user.projects[projectId]) {
                                    user.projects[projectId].priority =
                                        "Normal";
                                }
                            }
                        );
                    }
                }
            }
        },
    },
    actions: {
        async setUserOvertimeToggleEnabled(_, { userId, enabled }) {
            try {
                await update(ref(db, `users/${userId}`), {
                    overtimeToggleEnabled: !!enabled,
                });
            } catch (e) {
                console.error("Error updating user overtimeToggleEnabled:", e);
                throw e;
            }
        },
        /**
         * Resets the current user's project priorities to 'Normal' without triggering reactivity-based notifications.
         * This is used during logout and end-of-day to cleanly zero out priorities without affecting UI observers.
         *
         * @param {Object} context - Vuex context.
         */
        async resetUserProjectsPriorities({ state, commit }) {
            try {
                const userId = auth.currentUser?.uid;
                if (!userId || !state.user?.projects) return;

                // 1) Set flag to disable reactive watchers
                state.priorityResetInProgress = true;

                const updates = {};
                const updatedProjects = { ...state.user.projects };

                // 2) Only reset projects that are currently "Highest"
                Object.entries(updatedProjects).forEach(
                    ([projectId, projectData]) => {
                        const currentPriority = projectData?.priority;
                        if (currentPriority === "Highest") {
                            updates[
                                `users/${userId}/projects/${projectId}/priority`
                            ] = "Normal";
                            updatedProjects[projectId] = {
                                ...projectData,
                                priority: "Normal",
                            };
                        }
                    }
                );

                // Clear the flag if there are no updates to make
                if (Object.keys(updates).length === 0) {
                    state.priorityResetInProgress = false;
                    return;
                }

                // 3) Update Firebase
                await update(ref(db), updates);

                // 4) Update Vuex state (user.projects)
                commit("setUser", {
                    ...state.user,
                    projects: updatedProjects,
                });

                // Update the snapshot used for priority comparison
                state.previousUserProjectPriorities = JSON.parse(
                    JSON.stringify(updatedProjects)
                );

                // 5) Allow watchers again on next tick
                setTimeout(() => {
                    state.priorityResetInProgress = false;
                }, 0);
            } catch (error) {
                console.error(
                    "Error resetting user project priorities:",
                    error
                );
                state.priorityResetInProgress = false;
            }
        },
        /**
         * Adds or removes a user from a project's `activeUsers` in both Firebase and Vuex,
         * using an atomic multi-path update to avoid in-between states and a per-user queue
         * to prevent overlapping switches for the same user.
         *
         * Behavior:
         * 1) If `oldProject.projectId` exists → remove the user from that project's activeUsers.
         * 2) If `newProject.projectId` exists and is not "SYSTEM_LUNCH-BREAK" → add the user to that project's activeUsers.
         * 3) Both operations are sent as a single atomic RTDB update (one `update(ref(db), updates)`).
         * 4) Registers/cleans onDisconnect handlers.
         * 5) Commits local Vuex mutations idempotently.
         * 6) Execution is serialized per user via `enqueueUserTask` to avoid races.
         *
         * @param {{projectId?: string}|null} payload.oldProject
         *    The previous project object; if it has a `projectId`, the user will be removed from it.
         * @param {{projectId?: string}|null} payload.newProject
         *    The target project object; if it has a `projectId` (and not the system lunch ID), the user will be added to it.
         * @param {string} payload.userId     The UID of the user to update.
         * @param {string|null} payload.name  User’s first name (stored under activeUsers when adding).
         * @param {string|null} payload.surname User’s surname (stored under activeUsers when adding).
         * @returns {Promise<void>}
         *    Resolves when the queued, atomic update completes (or rejects on failure).
         *
         */
        async addRemoveActiveUser(
            { commit },
            { oldProject, newProject, userId, name, surname }
        ) {
            return enqueueUserTask(userId, async () => {
                try {
                    // --- Atomic DB updates (single round-trip) ---
                    const updates = {};

                    if (oldProject?.projectId) {
                        updates[
                            `projects/${oldProject.projectId}/activeUsers/${userId}`
                        ] = null;
                    }

                    const projectId = newProject?.projectId;
                    let shouldAddNew = false;
                    if (projectId) {
                        // ensures it exists and isn’t empty
                        if (projectId !== "SYSTEM_LUNCH-BREAK") {
                            shouldAddNew = true;
                        }
                    }

                    if (shouldAddNew) {
                        updates[
                            `projects/${newProject.projectId}/activeUsers/${userId}`
                        ] = { name, surname };
                    }

                    if (Object.keys(updates).length) {
                        await update(ref(db), updates); // atomic write
                    }

                    // --- Local Vuex state (idempotent) ---
                    if (oldProject?.projectId) {
                        commit("removeActiveUserLocal", {
                            projectId: oldProject.projectId,
                            userId,
                        });
                    }
                    if (shouldAddNew) {
                        commit("addActiveUserLocal", {
                            projectId: newProject.projectId,
                            userId,
                            user: { name, surname },
                        });
                    }
                } catch (error) {
                    console.error("[Vuex] addRemoveActiveUser failed:", error);
                }
            });
        },
        /**
         * Fetches the list of companies from Firebase Realtime Database.
         * - If companies exist, commits them to the Vuex state using the "setCompanies" mutation.
         * - If no companies are found, commits an empty array to the state.
         *
         * @async
         * @function fetchCompanies
         * @param {Object} context - The Vuex context object.
         * @param {Function} context.commit - The Vuex commit function to call mutations.
         * @returns {Promise<void>}
         * @throws {Error} If an error occurs while fetching companies from the database.
         */
        async fetchCompanies({ commit }) {
            try {
                const companiesRef = ref(db, "companies");
                const snapshot = await get(companiesRef);

                if (snapshot.exists()) {
                    const companies = Object.values(snapshot.val());
                    commit("setCompanies", companies);
                } else {
                    console.warn("No companies found in the database.");
                    commit("setCompanies", []); // Set an empty array if no companies exist
                }
            } catch (error) {
                console.error("Error fetching companies:", error);
            }
        },

        /**
         * Start listening for sessionId changes in Firebase
         * @param {Object} context - Vuex context
         * @param {String} userId - Current user's ID
         */
        async startSessionListener({ state, commit }, userId) {
            if (unsubscribes.session) unsubscribes.session();

            const sessionIdRef = ref(db, `users/${userId}/sessionId`);

            // Step 1: Fetch sessionId from Firebase to ensure it's synced
            const snapshot = await get(sessionIdRef);
            const sessionIdInDb = snapshot.val();

            // Step 2: Synchronize Vuex state if necessary
            if (state.sessionId === null) {
                commit("setSessionId", sessionIdInDb);
            }

            // Step 3: Start the listener
            unsubscribes.session = onValue(sessionIdRef, async (snapshot) => {
                const updatedSessionIdInDb = snapshot.val();

                // Ignore if both session IDs are null
                if (!updatedSessionIdInDb && !state.sessionId) {
                    return;
                }

                // Log out if there's a mismatch
                if (updatedSessionIdInDb !== state.sessionId) {
                    try {
                        await signOut(auth);
                        commit("setSessionId", null);
                        alert(
                            "You have been logged out because you signed in on another device."
                        );
                        window.location.reload();
                    } catch (error) {
                        console.error("Error during forced sign-out:", error);
                    }
                }
            });
        },
        /**
         * Deletes a time entry for a specific user from the database.
         *
         * @param {Object} _ - Placeholder for the Vuex context object (not used).
         * @param {Object} payload (userId, entryId) - The payload containing the required parameters.
         */
        async deleteTimeEntry(_, { userId, entryId }) {
            if (!userId || !entryId) {
                console.error(
                    "Missing userId or entryId for deleting time entry"
                );
            }

            try {
                // Reference to the specific time entry in the database
                const entryRef = ref(
                    db,
                    `users/${userId}/timeEntries/${entryId}`
                );
                // Remove the entry from the database
                await remove(entryRef);
            } catch (error) {
                console.error("Failed to delete time entry:", error);
                throw error;
            }
        },
        async updateTimeEntryInterval(
            _,
            { userId, entryId, startTime, endTime }
        ) {
            try {
                // Construct the Firebase path for the time entry
                const entryRef = ref(
                    db,
                    `users/${userId}/timeEntries/${entryId}`
                );

                // Update the startTime and endTime in Firebase
                await update(entryRef, {
                    startTime,
                    endTime,
                    modifiedAt: { ".sv": "timestamp" },
                });
            } catch (error) {
                console.error("Error updating interval in Firebase:", error);
                throw error;
            }
        },
        async updateTimeEntryProject(_, { userId, entryId, newProjectId }) {
            if (!userId || !entryId || !newProjectId) {
                console.error("Invalid userId, entryId, or newProjectId:", {
                    userId,
                    entryId,
                    newProjectId,
                });
                throw new Error(
                    "Invalid data for updating time entry project."
                );
            }

            try {
                // Reference the specific entry under the user's timeEntries
                const entryRef = ref(
                    db,
                    `users/${userId}/timeEntries/${entryId}`
                );

                await update(entryRef, {
                    projectId: newProjectId,
                    modifiedAt: { ".sv": "timestamp" },
                }); // Update the projectId field
            } catch (error) {
                console.error("Error updating entry in Firebase:", error);
                throw error;
            }
        },
        async addTimeEntry(_, { userId, newEntry }) {
            try {
                const entriesRef = ref(db, `users/${userId}/timeEntries`);
                const payload = {
                    ...newEntry,
                    modifiedAt: { ".sv": "timestamp" },
                };
                const newEntryRef = await push(entriesRef, payload);

                // Return the new entry along with its generated ID
                return { id: newEntryRef.key, ...newEntry };
            } catch (error) {
                console.error("Error adding time entry:", error);
                throw error;
            }
        },
        /**
         * Start listening for admin-triggered restarts.
         * Compares to localStorage to avoid reload loops.
         */
        startApplicationRestartListener() {
            // ensure only one active subscription
            if (unsubscribes.appRestart) unsubscribes.appRestart();

            unsubscribes.appRestart = fsStartApplicationRestartListener(
                (newRestartTimestamp) => {
                    const lastSeenKey = "app_restart_last_seen_timestamp";
                    const lastSeenRestartTimestamp = Number(
                        localStorage.getItem(lastSeenKey) || 0
                    );

                    if (newRestartTimestamp > lastSeenRestartTimestamp) {
                        // remember before reloading so we don't loop
                        localStorage.setItem(
                            lastSeenKey,
                            String(newRestartTimestamp)
                        );
                        alert(
                            "An update is available. The app will reload now."
                        );
                        window.location.reload();
                    }
                }
            );
        },
        startProjectsListener({ commit, dispatch, state }, userId) {
            if (unsubscribes.projects) unsubscribes.projects();
            // Reset so the first snapshot after (re)subscribe can refresh users if needed.
            previousManagedProjectIdsKey = "";
            unsubscribes.projects = fsStartProjectsListener(
                ({ managedProjects, workingProjects, allProjects }) => {
                    commit("setManagedProjects", managedProjects);
                    commit("setWorkingProjects", workingProjects);
                    // Also update allProjects for display purposes (project name lookups)
                    if (allProjects) {
                        commit("setAllProjects", allProjects);
                    }

                    // Team-lead user lists must track managed project membership.
                    const viewUsers = state.rights?.viewUsers;
                    if (viewUsers === "mine") {
                        const nextKey = (managedProjects || [])
                            .map((p) => p.projectId)
                            .filter(Boolean)
                            .sort()
                            .join("|");
                        if (nextKey !== previousManagedProjectIdsKey) {
                            previousManagedProjectIdsKey = nextKey;
                            dispatch("startUsersListener");
                        }
                    }
                }
            );
        },
        startUsersListener({ commit }) {
            if (unsubscribes.users) unsubscribes.users();
            unsubscribes.users = fsStartUsersListener((users) => {
                commit("setUsers", users);
            });
        },
        async startUserListener({ commit }, userId) {
            if (unsubscribes.user) unsubscribes.user();
            unsubscribes.user = fsStartUserListener(userId, async (user) => {
                if (user) {
                    commit("setUser", user);
                    // code block for when admin uses end of day on a user
                    try {
                        // "Open" = any entry without endTime
                        const hasOpenEntry = Object.values(
                            user.timeEntries || {}
                        ).some((e) => !e.endTime);
                        if (!hasOpenEntry) {
                            // Clear the local-only flags that were set when starting a project
                            commit("setActiveProject", null);
                            commit("setStartTime", null);
                            commit("setLastEntryKey", null);
                        }
                    } catch (_) {
                        // no-op
                    }
                } else {
                    commit("setUser", null);
                    commit("setRights", null);
                }
            });
        },

        //Updates a project both in Firebase and the Vuex store.
        async updateProjectInStore(
            { commit, state, dispatch },
            { projectId, updatedProject, previousProject = null }
        ) {
            try {
                const prev =
                    previousProject ||
                    (state.managedProjects || []).find(
                        (p) => p.projectId === projectId
                    ) ||
                    (state.allProjects || []).find(
                        (p) => p.projectId === projectId
                    ) ||
                    null;

                const wasActive = prev ? isProjectActive(prev) : false;
                const willBeInactive = updatedProject?.isActive === false;

                await updateProjectInDatabase(projectId, updatedProject);
                commit("updateProject", { projectId, updatedProject });

                // Children inherit the parent's Odoo id when the parent changes it.
                if (Object.prototype.hasOwnProperty.call(updatedProject, "odooId")) {
                    const nextOdooId = updatedProject.odooId || "";
                    const children = (state.allProjects || []).filter(
                        (p) => p.projectId && p.parentProjectId === projectId
                    );
                    for (const child of children) {
                        if ((child.odooId || "") === nextOdooId) continue;
                        const childUpdate = { odooId: nextOdooId };
                        await updateProjectInDatabase(
                            child.projectId,
                            childUpdate
                        );
                        commit("updateProject", {
                            projectId: child.projectId,
                            updatedProject: childUpdate,
                        });
                    }
                }

                if (wasActive && willBeInactive) {
                    await dispatch("cascadeDeactivateProject", { projectId });
                }
            } catch (error) {
                console.error(
                    "Error updating project in store and Firebase:",
                    error
                );
                throw error;
            }
        },

        /**
         * When a project is deactivated: end open entries on that project,
         * clear activeUsers presence, and unassign the project from all users.
         * Prefers in-memory store users; falls back to RTDB only if empty.
         */
        async cascadeDeactivateProject({ state, commit }, { projectId }) {
            if (!projectId) return;

            let usersList = state.users || [];
            if (!usersList.length) {
                const usersSnap = await get(ref(db, "users"));
                const usersMap = usersSnap.exists() ? usersSnap.val() : {};
                usersList = Object.entries(usersMap).map(([uid, user]) => ({
                    ...user,
                    uid,
                }));
            }

            const updates = {};

            usersList.forEach((user) => {
                const userId = user.uid || user.id;
                if (!userId) return;

                if (user?.projects?.[projectId]) {
                    updates[`users/${userId}/projects/${projectId}`] = null;
                }

                const entries = user?.timeEntries || {};
                Object.entries(entries).forEach(([entryId, entry]) => {
                    if (
                        entry &&
                        entry.projectId === projectId &&
                        entry.startTime &&
                        !entry.endTime
                    ) {
                        updates[
                            `users/${userId}/timeEntries/${entryId}/endTime`
                        ] = { ".sv": "timestamp" };
                        updates[
                            `users/${userId}/timeEntries/${entryId}/modifiedAt`
                        ] = { ".sv": "timestamp" };
                    }
                });
            });

            updates[`projects/${projectId}/activeUsers`] = null;

            if (Object.keys(updates).length) {
                await update(ref(db), updates);
            }

            if (state.activeProject?.projectId === projectId) {
                commit("setActiveProject", null);
                commit("setStartTime", null);
                commit("setLastEntryKey", null);
            }
        },

        async updateUserRole({ state }, { userId, role }) {
            if (!userId || !role) {
                throw new Error("userId and role are required");
            }
            if (role !== "operator" && role !== "teamLead") {
                throw new Error("Invalid role");
            }
            if (state.user?.role !== "admin") {
                throw new Error("Only admins can change user roles");
            }

            const updates = {
                [`users/${userId}/role`]: role,
            };

            // Demote: remove this user from every project's teamLeaders[].
            if (role === "operator") {
                let projects = state.allProjects?.length
                    ? state.allProjects
                    : state.managedProjects || [];
                if (!projects.length) {
                    const snap = await get(ref(db, "projects"));
                    if (snap.exists()) {
                        projects = Object.entries(snap.val()).map(
                            ([id, project]) => ({
                                ...project,
                                projectId: id,
                            })
                        );
                    }
                }
                projects.forEach((project) => {
                    if (!project?.projectId) return;
                    if (!isProjectTeamLead(project, userId)) return;
                    const next = getProjectTeamLeaders(project).filter(
                        (uid) => uid !== userId
                    );
                    updates[`projects/${project.projectId}/teamLeaders`] = next;
                });
            }

            await update(ref(db), updates);
        },

        async endTimeEntryForUser(_, { userId, entryId, isEndOfDay = true }) {
            if (!userId || !entryId) {
                console.error(
                    "[Vuex] endTimeEntryForUser missing userId/entryId"
                );
                return;
            }
            try {
                const entryRef = ref(
                    db,
                    `users/${userId}/timeEntries/${entryId}`
                );
                const updateData = {
                    endTime: { ".sv": "timestamp" },
                    modifiedAt: { ".sv": "timestamp" },
                };
                if (isEndOfDay) {
                    updateData.endOfSession = true;
                }
                await update(entryRef, updateData);
            } catch (error) {
                console.error("[Vuex] endTimeEntryForUser failed:", error);
            }
        },

        async resetUserProjectsPriorityForUser({ state }, { userId }) {
            try {
                const userProjectsRef = ref(db, `users/${userId}/projects`);
                const snap = await get(userProjectsRef);
                const projects = snap.val();
                if (!projects) return;

                const updates = {};
                // Reset ALL priorities to "Normal" for that user (matches your requirement)
                Object.keys(projects).forEach((pid) => {
                    updates[`${pid}/priority`] = "Normal";
                });

                await update(userProjectsRef, updates);
            } catch (error) {
                console.error(
                    "[Vuex] resetUserProjectsPriorityForUser failed:",
                    error
                );
            }
        },

        /**
         * Resets priorities of all users' projects to 'Normal' in Firebase and Vuex state.
         * @param {Object} context - Vuex context object.
         */
        async resetAllUsersProjectPriorities({ commit }) {
            try {
                const usersRef = ref(db, `users`);

                // Fetch all users and their projects
                const usersSnapshot = await get(usersRef);
                const allUsers = usersSnapshot.val();

                if (!allUsers) {
                    console.warn("No users found in the database.");
                    return;
                }

                // Prepare updates for Firebase
                const updates = {};
                for (const userId in allUsers) {
                    const userProjects = allUsers[userId].projects;
                    if (!userProjects) continue;

                    for (const projectId in userProjects) {
                        updates[
                            `users/${userId}/projects/${projectId}/priority`
                        ] = "Normal";
                    }
                }

                if (Object.keys(updates).length === 0) {
                    console.warn("No project priorities to reset.");
                    return;
                }

                // Apply updates to Firebase
                await update(ref(db), updates);

                // Commit Vuex mutation to reflect changes in local state
                commit("updateUsersProjects", allUsers);
            } catch (error) {
                console.error(
                    "Error resetting project priorities for all users:",
                    error
                );
            }
        },

        /**
         * Resets priorities of specified projects to 'Normal' in both the database and Vuex state.
         * @param {Object} context - The Vuex context object.
         * @param {Array<string>} projectIds - Array of project IDs to reset.
         */
        async resetPrioritiesOfProjects({ commit }, projectIds) {
            try {
                // Prepare the updates for the specified projects
                const updates = {};
                projectIds.forEach((projectId) => {
                    updates[`projects/${projectId}/priority`] = "Normal";
                });

                // Update the database in a single batch
                await updateMultipleProjectsInDatabase(updates);

                // Commit the mutation to update the Vuex state
                commit("resetPrioritiesOfProjects", projectIds);
            } catch (error) {
                console.error("Error resetting project priorities:", error);
                throw error;
            }
        },
        setUser({ commit }, user) {
            commit("setUser", user); // Use commit to call the setUser mutation
        },
        /**
         * Sets the active project in the store.
         * @param {Function} commit - Vuex commit function to commit mutations.
         * @param {Object} project - The project object.
         */
        setActiveProject({ commit }, project) {
            commit("setActiveProject", project);
        },
        async endCurrentProject({ state, commit }, isEndOfDay = false) {
            const { activeProject, lastEntryKey } = state;

            if (activeProject && lastEntryKey) {
                const userId = auth.currentUser?.uid;
                if (!userId) {
                    console.error(
                        "[Vuex] Cannot end project: userId is undefined"
                    );
                    return;
                }

                try {
                    // Dynamically build the object to update
                    const updateData = {
                        endTime: { ".sv": "timestamp" }, // Always add endTime
                        modifiedAt: { ".sv": "timestamp" },
                    };

                    if (isEndOfDay) {
                        updateData.endOfSession = true; // Add endOfDay marker only if requested
                    }

                    // Perform the update with the dynamic object
                    await update(
                        ref(db, `users/${userId}/timeEntries/${lastEntryKey}`),
                        updateData
                    );

                    // Commit state changes
                    commit("setActiveProject", null);
                    commit("setLastEntryKey", null);
                    commit("setStartTime", null);
                } catch (error) {
                    console.error(
                        "[Vuex] Error updating the last time entry:",
                        error
                    );
                }
            }
        },
        async createTimeEntry(_, { userId, projectId, overtime = false }) {
            return enqueueUserTask(userId, async () => {
                const userTimeEntriesRef = ref(
                    db,
                    `users/${userId}/timeEntries`
                );
                const newTimeEntryRef = push(userTimeEntriesRef);

                const newTimeEntry = {
                    projectId,
                    startTime: { ".sv": "timestamp" },
                    endTime: null,
                    endOfSession: null,
                    overtime: !!overtime,
                    modifiedAt: { ".sv": "timestamp" },
                };

                await set(newTimeEntryRef, newTimeEntry);

                // read back to get the server-assigned startTime
                const snapshot = await get(newTimeEntryRef);
                const entryData = snapshot.val();
                const startTime = entryData?.startTime;

                return { key: newTimeEntryRef.key, startTime };
            });
        },

        async endCurrentTimeEntry(
            { commit },
            { isEndOfDay = false, activeEntry, userId }
        ) {
            if (!userId) {
                console.error(
                    "[Vuex] Cannot end time entry: userId is undefined."
                );
                return;
            }

            if (!activeEntry) {
                console.warn("[Vuex] No active time entry found.");
                return;
            }

            try {
                // Build the update data
                const updateData = {
                    endTime: { ".sv": "timestamp" },
                    modifiedAt: { ".sv": "timestamp" },
                };

                if (isEndOfDay) {
                    updateData.endOfSession = true;
                }

                // Perform the update in Firebase
                await update(
                    ref(db, `users/${userId}/timeEntries/${activeEntry.uid}`),
                    updateData
                );

                // Commit state changes
                commit("setActiveProject", null);
                commit("setStartTime", null);
                commit("setLastEntryKey", null);
            } catch (error) {
                console.error("[Vuex] Error updating time entry:", error);
            }
        },

        stopAllListeners() {
            Object.values(unsubscribes).forEach((fn) => fn && fn());
        },

        //Updates the overtime flag of a time entry in the database.
        async updateTimeEntryOvertime(_, { userId, entryId, overtime }) {
            try {
                const entryRef = ref(
                    db,
                    `users/${userId}/timeEntries/${entryId}`
                );
                await update(entryRef, {
                    overtime: overtime,
                    modifiedAt: { ".sv": "timestamp" },
                });
            } catch (error) {
                console.error("Error updating overtime flag:", error);
                throw error;
            }
        },
        async updateTimeEntryDescription(_, { userId, entryId, description }) {
            try {
                const entryRef = ref(
                    db,
                    `users/${userId}/timeEntries/${entryId}`
                );
                await update(entryRef, {
                    description: description || null,
                    modifiedAt: { ".sv": "timestamp" },
                });
            } catch (error) {
                console.error("Error updating description:", error);
                throw error;
            }
        },

        //Updates the user's status both in Firebase and the Vuex store.
        async updateUserStatus({ commit, state }, status) {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    console.error("User not authenticated.");
                    return;
                }
                const userId = currentUser.uid;

                // Step 1: Update Firebase
                const userRef = ref(db, `users/${userId}`);
                await update(userRef, { userStatus: status });

                // Step 2: Commit mutation to update Vuex store
                commit("setUserStatus", status);
            } catch (error) {
                console.error("Error updating user status:", error);
            }
            // Step 2: Commit mutation to update Vuex store
        },
        async fetchProjects({ commit }) {
            const projects = await fetchAllProjects();
            commit("setAllProjects", projects);
        },
        async updateAccountStatus({ commit }, { userId, status }) {
            try {
                const userRef = ref(db, `users/${userId}`);
                await update(userRef, { accountStatus: status });
                commit("setAccountStatus", { userId, status });
            } catch (error) {
                console.error("Error updating account status:", error);
            }
        },

        async updateTimeEntryFlag(_, { userId, entryId, endOfSession }) {
            try {
                // Reference the time entry in the Realtime Database
                const entryRef = ref(
                    db,
                    `users/${userId}/timeEntries/${entryId}`
                );

                // Update the `endOfSession` field
                await update(entryRef, { endOfSession });
            } catch (error) {
                console.error("Failed to update endOfSession flag:", error);
            }
        },

        signOutFromStore({ commit, state }) {
            // 1) Session & UI flags
            commit("setSessionId", null);
            commit("setActiveProject", null);
            commit("setStartTime", null);
            commit("setIsOnLunchBreak", false);
            commit("setLastEntryKey", null);

            // 2) Collections derived from the signed-in user
            commit("setManagedProjects", null);
            commit("setWorkingProjects", null);
            commit("setAllProjects", null);
            commit("setUsers", null);
            commit("setCompanies", null);
            commit("setRights", null);

            // 3) Local UI / watcher helpers
            state.priorityResetInProgress = false;
            state.previousUserProjectPriorities = {};
            state.loading = false;

            // 4) Finally clear the user object last
            // (so earlier commits that rely on state.user won't crash)
            commit("setUser", null);
        },
    },
});
